import { createAsyncThunk } from "@reduxjs/toolkit";
import { baseUrl, headersDefault } from ".";

import dayjs from "dayjs";

import {
  orderDatesSubArrays,
  substractOneDayToEndDatesSubArrays,
  getNotIncludedDates,
  synchronizeDatetoString,
  subtractOneDayToDateString,
} from "../../services/functions";
import axios from "axios";
import { axiosGetRead, axiosRead } from "../../services/readRequest";
import { TRIAL_CHECKPOINT_ERROR_CODE } from "../../utils/trialCheckpoint";

export const getFoodReplacements = createAsyncThunk(
  "dietPlan/getFoodReplacements",
  async (
    {
      cycle_id,
      item_id,
      type,
      page = 1,
      from_recipe,
      preparation_time,
      exclude_food_ids,
      favorites_only,
      context_type,
    },
    { rejectWithValue },
  ) => {
    try {
      let url = "";
      const queryParams = new URLSearchParams();
      queryParams.set("page", String(page));
      if (context_type) queryParams.set("context_type", context_type);

      // Filter params for recipe replacements
      if (preparation_time) {
        queryParams.set("preparation_time", preparation_time);
      }
      if (exclude_food_ids && exclude_food_ids.length > 0) {
        exclude_food_ids.forEach((id) => queryParams.append("exclude_food_ids[]", String(id)));
      }
      if (favorites_only) {
        queryParams.set("favorites_only", "1");
      }

      // Both endpoints now follow /api/v1/{type}-replacements/{cycleId}/{itemId}?page={page}
      if (type === "recipe") {
        url = `${baseUrl}/api/v1/recipe-replacements/${cycle_id}/${item_id}?${queryParams.toString()}`;
      } else {
        // type === 'food'
        if (from_recipe) {
          queryParams.set("from_recipe", String(from_recipe));
        }
        url = `${baseUrl}/api/v1/food-replacements/${cycle_id}/${item_id}?${queryParams.toString()}`;
      }

      const response = await axiosGetRead(url, {
        headers: headersDefault(),
      });

      return {
        data: response.data.data,
        filters: response.data.filters ?? null,
        meta: {
          current_page: response.data.current_page,
          last_page: response.data.last_page,
          total: response.data.total,
          user_has_favorites: Boolean(response.data.user_has_favorites),
        },
        isFirstPage: page === 1,
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "Errore generico"
      );
    }
  }
);

export const deleteShoppingListCheck = createAsyncThunk(
  "dietPlan/deleteShoppingListCheck",
  async (id) => {
    const response = await fetch(
      `${baseUrl}/api/v1/master-club-shopping-list-checks/${id}`,
      {
        method: "DELETE",
        headers: headersDefault(),
      }
    );

    console.log("response", response);
    if (response.status !== 200)
      throw new Error(
        (response.message || (await response.json()).error) ?? "GenericError"
      );

    return await response.json();
  }
);

// Adoc patient with Premium: save the plan mode picked in Percorsi. The
// backend answers with the refreshed adoc_patient block (effective_mode, badge).
export const savePlanModePreference = createAsyncThunk(
  "dietPlan/savePlanModePreference",
  async (mode, { rejectWithValue }) => {
    const response = await fetch(`${baseUrl}/api/v1/adoc/plan-mode`, {
      method: "POST",
      headers: headersDefault(),
      body: JSON.stringify({ mode }),
    });
    const data = await response.json().catch(() => null);
    if (!response.ok) return rejectWithValue(data?.error ?? data?.message ?? "GenericError");
    return data;
  }
);

// NEW: Reset Shopping List Action
export const resetShoppingListChecks = createAsyncThunk(
  "dietPlan/resetShoppingListChecks",
  // One object argument: a thunk's second parameter is thunkAPI, not a value.
  async ({ route_plan_id, adoc_diet_plan_id = null }) => {
    const body = adoc_diet_plan_id ? JSON.stringify({ adoc_diet_plan_id }) : undefined;
    const response = await fetch(
      `${baseUrl}/api/master-club-shopping-list-checks/reset/${route_plan_id}`,
      {
        method: "DELETE",
        headers: headersDefault(),
        body,
      }
    );

    if (response.status !== 200)
      throw new Error(
        (response.message || (await response.json()).error) ?? "GenericError"
      );

    return await response.json();
  }
);

/**
 * A 400 from fetch-diet-plan is an answer, not a failure: the backend returns it
 * when the date has no plan, or falls outside the percorso. Anything else — a
 * 403 (the app API returns them in bursts, several endpoints at once, clearing
 * a few seconds later), a 5xx, a dropped mobile connection — is transient.
 *
 * That distinction did not exist before, and one attempt was all the menu got:
 * the page rendered "Menu non disponibile" and nothing re-ran the fetch, so a
 * blip looked exactly like a missing menu until the member changed date or
 * restarted the app. This is the "Menu non disponibile" members keep reporting.
 */
const TRANSIENT_MENU_RETRY_DELAYS_MS = [400, 900, 1800];

const isGenuinelyUnavailable = (status) => status === 400;

// New action for the updated diet plan endpoint
export const getDietPlanV1 = createAsyncThunk(
  "dietPlan/getDietPlanV1",
  async ({ date, shoppingList = false, date_end = null }, { rejectWithValue, getState }) => {
    // Adoc patients use a dedicated endpoint; everyone else uses the MC route.
    const planMode = getState()?.dietPlan?.planMode ?? 'master_club';
    const endpoint = planMode === 'adoc'
      ? `${baseUrl}/api/v1/adoc/fetch-diet-plan`
      : `${baseUrl}/api/v1/master-club-route/fetch-diet-plan`;

    // Prepare query parameters
    const params = {
      date: synchronizeDatetoString(date),
    };

    // Add shopping list parameters if needed
    if (shoppingList) {
      params.shoppingList = true;
      if (date_end) {
        params.date_end = synchronizeDatetoString(date_end);
      }
    }

    let response;
    let lastError;

    for (
      let attempt = 0;
      attempt <= TRANSIENT_MENU_RETRY_DELAYS_MS.length;
      attempt++
    ) {
      try {
        response = await axiosRead(
          {
            method: "GET",
            url: endpoint,
            headers: headersDefault(),
            params: params,
            data: {}, // Empty data object to ensure proper request handling
          },
          // Retries must reach the network rather than join the failed call
          // already cached under the same key.
          attempt > 0 ? { dedupe: false } : undefined
        );
        lastError = null;
        break;
      } catch (error) {
        lastError = error;

        const status = error?.response?.status ?? null;
        if (isGenuinelyUnavailable(status)) break;
        // Too many requests: retrying within seconds only spends more of the
        // same minute's allowance, which Home and the feed share (07.10.2026).
        if (status === 429) break;
        // The FREE trial checkpoint: waiting won't open it, confirming will.
        if (error?.response?.data?.code === TRIAL_CHECKPOINT_ERROR_CODE) break;

        const delay = TRANSIENT_MENU_RETRY_DELAYS_MS[attempt];
        if (delay === undefined) break;

        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }

    // Mode may have flipped while we were on the wire (MC ↔ Adoc). Discard
    // before the reducer can paint the wrong menu.
    if (getState()?.dietPlan?.planMode !== planMode) {
      return rejectWithValue({ stale: true, planMode });
    }

    if (lastError) {
      const status = lastError?.response?.status ?? null;
      return rejectWithValue({
        status,
        code: lastError?.response?.data?.code ?? null,
        message:
          lastError?.response?.data?.error ??
          lastError?.message ??
          "GenericError",
        // Lets the reducer discard this result if planMode changed mid-flight.
        planMode,
      });
    }

    if (response.status !== 200)
      return rejectWithValue({
        status: response.status,
        message: response.message ?? "GenericError",
        planMode,
      });

    const data = response.data;

    // If it's a shopping list request, return the shopping list data
    if (shoppingList) {
      return {
        type: "shoppingList",
        shoppingList: data,
        date: synchronizeDatetoString(date),
        date_end: date_end ? synchronizeDatetoString(date_end) : null,
        planMode,
      };
    }

    // For regular diet plan, process the new structure
    return {
      type: "dietPlan",
      dietPlan: data,
      date: synchronizeDatetoString(date),
      planMode,
    };
  }
);

export const getDietPlan = createAsyncThunk(
  "dietPlan/getDietPlan",
  async (date) => {
    const params = {};

    if (date) params.date = synchronizeDatetoString(date);

    const response = await axiosGetRead(
      `${baseUrl}/api/master-club-route/subscriber-route`,
      {
        headers: headersDefault(),
        params,
      }
    );

    if (response.status !== 200)
      throw new Error(
        (response.message || (await response.json()).error) ?? "GenericError"
      );

    const data = response.data;

    const newDietPlan = Array(7)
      .fill()
      .map(() => []);

    data.master_club_plan.cycles.forEach(
      (food) => (newDietPlan[food.day - 1][food.meal_id - 1] = food)
    );
    const dates = [
      dayjs(data.master_club_route.start).utc(true).format("YYYY-MM-DD"),
      dayjs(data.master_club_route.end)
        .utc(true)
        .subtract(1, "day")
        .format("YYYY-MM-DD"),
    ];

    return {
      dietPlan: newDietPlan,
      dayDishes: data.master_club_plan.day_dishes,
      dates,
      foods: data.master_club_plan.foods,
      plan: data.master_club_plan.master_club_route_plan,
      route: data.master_club_route,
      shoppingList: data.master_club_plan.shoppingList,
    };
  }
);

export const getDatesToDisable = createAsyncThunk(
  "dietPlan/getDatesToDisable",
  async (includeFreeRoutes = false) => {
    const response = await fetch(
      `${baseUrl}/api/master-club-route/subscriber-routes`,
      {
        headers: headersDefault(),
      }
    );

    if (response.status !== 200)
      throw new Error(
        (response.message || (await response.json()).error) ?? "GenericError"
      );

    const data = await response.json();

    // if (data.length === 0) throw new Error('Empty Array')

    const dietRoutes = data
      .filter(
        (dietRoute) => includeFreeRoutes || "diet_plan_cycles" in dietRoute
      )
      .map((dietRoute) => [
        dayjs(dietRoute.route.start).format("YYYY-MM-DD"),
        dayjs(dietRoute.route.end).format("YYYY-MM-DD"),
      ]);

    dietRoutes.sort(orderDatesSubArrays);
    dietRoutes.forEach(substractOneDayToEndDatesSubArrays);

    if (dietRoutes.length === 0) {
      dietRoutes.push([
        dayjs().format("YYYY-MM-DD"),
        dayjs().format("YYYY-MM-DD"),
      ]);
    }

    const minDate = dietRoutes[0][0] + " 00:00:00";
    const maxDate = dietRoutes[dietRoutes.length - 1][1] + " 23:59:59";

    const routesToDisable = getNotIncludedDates(dietRoutes);

    return {
      routes: data,
      daysLimit: [minDate, maxDate],
      disableDates: routesToDisable,
    };
  }
);

export const shoppingListChecks = createAsyncThunk(
  "dietPlan/shoppingListChecks",
  async (values) => {
    const response = await fetch(
      `${baseUrl}/api/v1/master-club-shopping-list-checks`,
      {
        method: "POST",
        body: JSON.stringify(values),
        headers: headersDefault(),
      }
    );

    if (response.status !== 200)
      throw new Error(
        (response.message || (await response.json()).error) ?? "GenericError"
      );

    return await response.json();
  }
);
